import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  WORK_ORDER,
  AP_INVOICE,
  BUDGET,
  CONTRACT,
  PM_YEARLY_LIMIT,
  BLANKET_LIMIT,
  BOARD_LEVEL_APPROVAL,
  PM_APPROVAL_LIMIT,
  SINGLE_APPROVAL,
} from "./consts";
import {
  useGetCommunityApprovalInfo,
  useUpdateApprovalMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { routes } from "@/routes/utils";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { schema } from "./validations";

const useStep4 = () => {
  const { id, action } = useParams();
  const form = useForm<any>({
    defaultValues: {
      [AP_INVOICE]: "1",
      [BUDGET]: "1",
      [CONTRACT]: "1",
      [WORK_ORDER]: "1",
      [PM_YEARLY_LIMIT]: "1",
      [BLANKET_LIMIT]: "1",
      [BOARD_LEVEL_APPROVAL]: "1",
      [PM_APPROVAL_LIMIT]: "1",
      [SINGLE_APPROVAL]: "1",
    },
    resolver: zodResolver(schema),
  });
  const navigate = useNavigate();
  const isEditDetails = action === "edit-details";
  const isAdd = action == "add";

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const {
    data: communityApprovalInfo,
    isLoading: isApprovalInfoLoading,
    isRefetching: isApprovalInfoRefetching,
  } = useGetCommunityApprovalInfo({
    community_id: id || "",
    queryParams: {
      enabled: !!(id && !isAdd),
    },
  });

  useEffect(() => {
    if (communityApprovalInfo) {
      form.reset({
        [AP_INVOICE]:
          communityApprovalInfo?.data?.[AP_INVOICE]?.toString() || "1",
        [BUDGET]: communityApprovalInfo?.data?.[BUDGET]?.toString() || "1",
        [CONTRACT]: communityApprovalInfo?.data?.[CONTRACT]?.toString() || "1",
        [WORK_ORDER]:
          communityApprovalInfo?.data?.[WORK_ORDER]?.toString() || "1",
        [PM_YEARLY_LIMIT]:
          communityApprovalInfo?.data?.[PM_YEARLY_LIMIT]?.toString() || "1",
        [BLANKET_LIMIT]:
          communityApprovalInfo?.data?.[BLANKET_LIMIT]?.toString() || "1",
        [BOARD_LEVEL_APPROVAL]:
          communityApprovalInfo?.data?.[BOARD_LEVEL_APPROVAL]?.toString() ||
          "1",
        [PM_APPROVAL_LIMIT]:
          communityApprovalInfo?.data?.[PM_APPROVAL_LIMIT]?.toString() || "1",
        [SINGLE_APPROVAL]:
          communityApprovalInfo?.data?.[SINGLE_APPROVAL]?.toString() || "1",
      });
    }
  }, [communityApprovalInfo]);

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateApprovalMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        navigate(
          `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.details}`
        );
        toast.success("Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    updateInfo({ id: id || "", data: _data });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isUpdatePending,
    isEditDetails,
    id,
    isApprovalInfoLoading,
    isApprovalInfoRefetching,
  };
};

export default useStep4;
